import React, { useState, useMemo, useEffect } from "react";
import { Row, Col, Table, Badge, Input } from "reactstrap";
import { customersReport } from "../service";
import OrgSummaryPane from "./OrgSummaryPane";
import staticData from "../data/customers_sample.json";

const USE_STATIC_DATA = false;
const formatCurrency = (value) => `₹${(value / 100000).toFixed(2)}L`;

const sizeToAmov = {
  s: 1.5,
  m: 3,
  l: 5,
  xl: 7,
  xxl: 10,
};

const getRelativeTime = (input) => {
  if (!input) return "-";
  const date = new Date(input);
  const now = new Date();
  const diff = (date.getTime() - now.getTime()) / 1000;

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  const ranges = [
    { unit: "year", seconds: 31536000 },
    { unit: "month", seconds: 2592000 },
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 },
  ];

  for (let range of ranges) {
    if (Math.abs(diff) > range.seconds || range.unit === "second") {
      const delta = Math.round(diff / range.seconds);
      return rtf.format(delta, range.unit);
    }
  }
};
const getMonthYearString = (date) =>
  date.toLocaleString("default", { month: "short", year: "numeric" });

const getMonthYearDisplay = (date) => {
  return `${date.toLocaleString("default", { month: "short" })} '${date
    .getFullYear()
    .toString()
    .slice(-2)}`;
};

const getMonthRange = (start, end) => {
  const range = [];
  const startDate = new Date(start);
  const endDate = new Date(end);
  startDate.setDate(1);
  while (startDate <= endDate) {
    range.push(getMonthYearString(new Date(startDate)));
    startDate.setMonth(startDate.getMonth() + 1);
  }
  return range;
};

const transformRestructuredData = (data) => {
  const { organizations, orders } = data;

  return orders.map((order) => {
    const org = organizations[order.organization_id?.toString()];
    return {
      organization_id: order.organization_id,
      organization_name: org?.name,
      organization_status: org?.status,
      project_id: order.project_id,
      project_name: order.project_name,
      order_id: order.order_id,
      order_status: order.order_status,
      placed_date: order.placed_date,
      last_activity_at: org?.last_activity_at,
      value: order.value,
      real_order: order.real_order,
      size: org?.size,
    };
  });
};

const Customers = () => {
  const today = new Date();
  const defaultEnd = today.toISOString().split("T")[0];
  const defaultStart = new Date(today.getFullYear(), today.getMonth() - 5, 1)
    .toISOString()
    .split("T")[0];

  // Override list of organization IDs to mark as inactive
  const inactiveOrgIds = [275, 117, 226, 58];

  const [startDate, setStartDate] = useState(defaultStart);
  const [filters, setFilters] = useState({ size: "all" });
  const [endDate, setEndDate] = useState(defaultEnd);
  const [projectOrders, setProjectOrders] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "customer",
    direction: "asc",
  });

  const months = useMemo(
    () => getMonthRange(startDate, endDate),
    [startDate, endDate]
  );

  useEffect(() => {
    if (USE_STATIC_DATA) {
      setProjectOrders(transformRestructuredData(staticData));
    } else {
      customersReport().then((data) => {
        setProjectOrders(transformRestructuredData(data));
      });
    }
  }, []);

  const isRepeatCustomer = (orders) => {
    const dates = orders
      .filter((o) => o.value >= 25000) // Only consider orders >= ₹25,000
      .map((o) => new Date(o.placed_date))
      .sort((a, b) => a - b);

    if (dates.length < 2) return false;

    let anchor = dates[0];
    for (let i = 1; i < dates.length; i++) {
      const diffDays = (dates[i] - anchor) / (1000 * 60 * 60 * 24);
      if (diffDays >= 7) return true;
    }
    return false;
  };

  const computeAmovValue = (group) => {
    // Look for a size from any project in the group
    const size = group.items.find((i) => i.size)?.size;
    const sizeVal = sizeToAmov[size];

    if (!sizeVal) return null;

    const hasOrder = group.items.some((i) => i.real_order && i.placed_date);
    if (!hasOrder) return null;

    return {
      amov: sizeVal * 100000,
    };
  };

  const grouped = useMemo(() => {
    if (!Array.isArray(projectOrders)) return [];

    const map = new Map();
    projectOrders.forEach((p) => {
      if (p.organization_status !== "customer") return;
      if (!map.has(p.organization_id)) {
        map.set(p.organization_id, {
          customer: p.organization_name,
          size: p.size,
          organization_id: p.organization_id,
          items: [],
          lastActivity: null,
        });
      }
      map.get(p.organization_id).items.push(p);
      if (
        !map.get(p.organization_id).lastActivity ||
        (p.last_activity_at &&
          new Date(p.last_activity_at) >
            new Date(map.get(p.organization_id).lastActivity))
      ) {
        map.get(p.organization_id).lastActivity = p.last_activity_at;
      }
    });

    return Array.from(map.values());
  }, [projectOrders]);

  const isActiveCustomer = (group) => {
    // Check if this organization is in the override list
    if (inactiveOrgIds.includes(group.organization_id)) {
      return false;
    }

    const now = new Date();
    let hasRecentOrder = false;
    let hasRecentActivity = false;

    group.items
      .filter((item) => item.real_order)
      .forEach((item) => {
        if (item.placed_date) {
          const placed = new Date(item.placed_date);
          const twoMonthsAgo = new Date(now);
          twoMonthsAgo.setMonth(now.getMonth() - 2);
          if (placed >= twoMonthsAgo) {
            hasRecentOrder = true;
          }
        }
        if (item.last_activity_at) {
          const lastActivity = new Date(item.last_activity_at);
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(now.getMonth() - 1);
          if (lastActivity >= oneMonthAgo) {
            hasRecentActivity = true;
          }
        }
      });
    return hasRecentOrder || hasRecentActivity;
  };

  const sortedGrouped = useMemo(() => {
    const sortFn = (a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "customer") {
        aVal = aVal?.toLowerCase() || "";
        bVal = bVal?.toLowerCase() || "";
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (sortConfig.key === "lastActivity") {
        const aDate = a.lastActivity ? new Date(a.lastActivity) : new Date(0);
        const bDate = b.lastActivity ? new Date(b.lastActivity) : new Date(0);
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      if (sortConfig.key.startsWith("month_")) {
        const month = sortConfig.key.slice(6);
        const aTotal = a.items
          .filter(
            (i) =>
              i.real_order &&
              i.placed_date &&
              getMonthYearString(new Date(i.placed_date)) === month
          )
          .reduce((sum, i) => sum + (i.value || 0), 0);
        const bTotal = b.items
          .filter(
            (i) =>
              i.real_order &&
              i.placed_date &&
              getMonthYearString(new Date(i.placed_date)) === month
          )
          .reduce((sum, i) => sum + (i.value || 0), 0);
        return sortConfig.direction === "asc"
          ? aTotal - bTotal
          : bTotal - aTotal;
      }

      if (sortConfig.key === "cartValue") {
        const aCart = a.items
          .filter((i) => i.real_order && !i.placed_date)
          .reduce((sum, i) => sum + (i.value || 0), 0);
        const bCart = b.items
          .filter((i) => i.real_order && !i.placed_date)
          .reduce((sum, i) => sum + (i.value || 0), 0);
        return sortConfig.direction === "asc" ? aCart - bCart : bCart - aCart;
      }

      if (sortConfig.key === "totalOrderValue") {
        const aTotal = a.items
          .filter((i) => i.real_order && i.placed_date)
          .reduce((sum, i) => sum + (i.value || 0), 0);
        const bTotal = b.items
          .filter((i) => i.real_order && i.placed_date)
          .reduce((sum, i) => sum + (i.value || 0), 0);
        return sortConfig.direction === "asc"
          ? aTotal - bTotal
          : bTotal - aTotal;
      }

      if (sortConfig.key === "amov") {
        const aVal = computeAmovValue(a);
        const bVal = computeAmovValue(b);

        if (aVal == null) return 1;
        if (bVal == null) return -1;

        return sortConfig.direction === "asc"
          ? aVal.amov - bVal.amov
          : bVal.amov - aVal.amov;
      }

      return 0;
    };

    const filteredGrouped = grouped.filter((g) => {
      if (filters.size === "all") return true;
      return g.size === filters.size;
    });

    const active = filteredGrouped.filter(isActiveCustomer);
    const inactive = filteredGrouped.filter((g) => !isActiveCustomer(g));

    return [...active.sort(sortFn), ...inactive.sort(sortFn)];
  }, [grouped, sortConfig, filters]);

  const totalAmov = useMemo(() => {
    return sortedGrouped
      .filter((group) => isActiveCustomer(group))
      .reduce((sum, group) => {
        const amovValue = computeAmovValue(group);
        return sum + (amovValue?.amov || 0);
      }, 0);
  }, [sortedGrouped]);

  const totals = useMemo(() => {
    return months.map((month) =>
      projectOrders
        .filter(
          (p) =>
            p.real_order &&
            p.organization_status === "customer" &&
            p.placed_date &&
            getMonthYearString(new Date(p.placed_date)) === month
        )
        .reduce((sum, p) => sum + (p.value || 0), 0)
    );
  }, [projectOrders, months]);

  const totalCart = useMemo(() => {
    return projectOrders
      .filter(
        (p) =>
          p.real_order && p.organization_status === "customer" && !p.placed_date
      )
      .reduce((sum, p) => sum + (p.value || 0), 0);
  }, [projectOrders]);

  const firstOrderTotals = useMemo(() => {
    const map = {};

    grouped.forEach((group) => {
      const placed = group.items.filter((i) => i.real_order && i.placed_date);
      if (placed.length === 0) return;

      const firstPlaced = new Date(
        Math.min(...placed.map((i) => new Date(i.placed_date)))
      );
      const monthKey = getMonthYearString(firstPlaced);
      const totalValue = placed
        .filter((i) => getMonthYearString(new Date(i.placed_date)) === monthKey)
        .reduce((sum, i) => sum + (i.value || 0), 0);

      map[monthKey] = (map[monthKey] || 0) + totalValue;
    });

    return months.map((month) => map[month] || 0);
  }, [grouped, months]);

  const handleSort = (key) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    setSortConfig({ key, direction: isAsc ? "desc" : "asc" });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const newAndChurnCounts = useMemo(() => {
    const monthToNew = {};
    const monthToChurned = {};

    grouped.forEach((group) => {
      const placed = group.items.filter((i) => i.real_order && i.placed_date);

      if (placed.length > 0) {
        const firstPlaced = placed
          .map((i) => new Date(i.placed_date))
          .sort((a, b) => a - b)[0];
        const firstMonth = getMonthYearString(firstPlaced);
        monthToNew[firstMonth] = (monthToNew[firstMonth] || 0) + 1;

        const lastPlaced = placed
          .map((i) => new Date(i.placed_date))
          .sort((a, b) => b - a)[0];
        const churnCutoff = new Date();
        churnCutoff.setMonth(churnCutoff.getMonth() - 3);
        if (lastPlaced < churnCutoff) {
          const lastMonth = getMonthYearString(lastPlaced);
          monthToChurned[lastMonth] = (monthToChurned[lastMonth] || 0) + 1;
        }
      }
    });

    return months.map((month) => ({
      newCount: monthToNew[month] || 0,
      churnedCount: monthToChurned[month] || 0,
    }));
  }, [grouped, months]);

  return (
    <Row className="border p-3 bg-white rounded">
      <Col className="d-flex gap-2 align-items-center mb-2">
        <label className="form-label mb-0 me-1 fw-bold">Filter by Month:</label>
        <Input
          type="month"
          value={startDate.slice(0, 7)}
          onChange={(e) => setStartDate(e.target.value + "-01")}
          className="w-auto"
        />
        <span className="mx-2">to</span>
        <Input
          type="month"
          value={endDate.slice(0, 7)}
          onChange={(e) => setEndDate(e.target.value + "-01")}
          className="w-auto"
        />
        <label className="form-label mb-0 ms-4 me-1 fw-bold">Size:</label>
        <select
          className="form-select form-select-sm w-auto me-3"
          value={filters.size}
          onChange={(e) => setFilters((f) => ({ ...f, size: e.target.value }))}
        >
          <option value="all">All</option>
          <option value="s">S</option>
          <option value="m">M</option>
          <option value="l">L</option>
          <option value="xl">XL</option>
          <option value="xxl">XXL</option>
        </select>
      </Col>
      <Table
        bordered
        responsive
        className="text-center align-middle"
        style={{ fontSize: "0.9rem" }}
      >
        <thead>
          <tr>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("customer")}
            >
              <div className="d-flex flex-column align-items-center">
                <div>
                  Customer {renderSortIcon("customer")}
                </div>
                {(() => {
                  const activeCount =
                    sortedGrouped.filter(isActiveCustomer).length;
                  const inactiveCount = sortedGrouped.length - activeCount;
                  return <div className="fw-normal fs-6">Active: {activeCount} / Inactive: {inactiveCount}</div>;
                })()}
              </div>
            </th>
            <th className="bg-secondary text-white">Size</th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("amov")}
            >
              AMOV {renderSortIcon("amov")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("totalOrderValue")}
            >
              Total {renderSortIcon("totalOrderValue")}
            </th>
            {months.map((month, idx) => {
              const newCount = newAndChurnCounts[idx].newCount;
              const churnedCount = newAndChurnCounts[idx].churnedCount;

              return (
                <th
                  key={idx}
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort(`month_${month}`)}
                >
                  <div className="d-flex flex-column align-items-center">
                    <div style={{ fontSize: "0.9em" }}>
                      {getMonthYearDisplay(new Date(month))}{" "}
                    </div>
                    <div className="fw-normal fs-6" style={{ opacity: 0.8 }}>
                      +{newCount} / -{churnedCount}
                    </div>
                  </div>
                </th>
              );
            })}
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("cartValue")}
            >
              In Cart {renderSortIcon("cartValue")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("lastActivity")}
            >
              Activity {renderSortIcon("lastActivity")}
            </th>
          </tr>
          {/* Total row */}
          <tr className="text-dark">
            <td className="bg-DDDDDD fw-bold"></td>
            <td className="bg-DDDDDD fw-bold"></td>
            <td className="bg-DDDDDD fw-bold">{formatCurrency(totalAmov)}</td>
            <td className="bg-DDDDDD fw-bold">
              {(() => {
                const totalPlaced = sortedGrouped.reduce(
                  (sum, group) =>
                    sum +
                    group.items
                      .filter((i) => i.real_order && i.placed_date)
                      .reduce((s, i) => s + (i.value || 0), 0),
                  0
                );
                return totalPlaced > 0 ? formatCurrency(totalPlaced) : "-";
              })()}
            </td>
            {totals.map((val, idx) => (
              <td key={idx} className="bg-DDDDDD fw-bold">
                {val > 0 ? formatCurrency(val) : "-"}
                {val > 0 && (
                  <div className="text-success small fw-semibold">
                    {formatCurrency(firstOrderTotals[idx])}
                  </div>
                )}
              </td>
            ))}
            <td className="bg-DDDDDD fw-bold">
              {totalCart > 0 ? formatCurrency(totalCart) : "-"}
            </td>
            <td className="bg-DDDDDD"></td>
          </tr>
        </thead>
        <tbody>
          {sortedGrouped.map((group, idx) => {
            const placed = group.items.filter(
              (i) => i.real_order && i.placed_date
            );
            const cart = group.items.filter(
              (i) => i.real_order && !i.placed_date
            );

            const cartValue = cart.reduce((sum, i) => sum + (i.value || 0), 0);
            const cartCount = cart.length;

            const firstPlaced =
              placed.length > 0
                ? new Date(
                    Math.min(...placed.map((i) => new Date(i.placed_date)))
                  )
                : null;

            const lastPlaced =
              placed.length > 0
                ? new Date(
                    Math.max(...placed.map((i) => new Date(i.placed_date)))
                  )
                : null;
            const hasChurnOrder = !isActiveCustomer(group);
            const amovResult = computeAmovValue(group);
            const isRepeat = amovResult != null;

            return (
              <tr
                key={idx}
                className={idx % 2 === 0 ? "table-light" : "table-white"}
                onClick={() => setSelectedOrgId(group.organization_id)}
                style={{ cursor: "pointer" }}
              >
                <td
                  className={`text-start fw-semibold ${
                    !isActiveCustomer(group) && "text-danger"
                  }`}
                >
                  {group.customer}
                  <span className="grey_letter fw-normal">
                    #{group.organization_id}
                  </span>
                </td>
                <td className="text-center small px-3 py-2">
                  {group.size?.toUpperCase?.() || "-"}
                </td>
                <td>
                  {isActiveCustomer(group) && amovResult && (
                    <div className="d-inline-flex align-items-center justify-content-center gap-1 text-primary fw-bold">
                      {formatCurrency(amovResult.amov)}
                    </div>
                  )}
                </td>
                <td className="text-end text-success fw-bold">
                  {placed.length > 0 ? (
                    <div className="d-inline-flex align-items-center justify-content-center gap-1">
                      {placed.length > 1 && (
                        <Badge
                          color="light"
                          pill
                          className="border text-muted small px-2"
                        >
                          {placed.length}
                        </Badge>
                      )}
                      <span>
                        {formatCurrency(
                          placed.reduce((sum, i) => sum + (i.value || 0), 0)
                        )}
                      </span>
                    </div>
                  ) : (
                    ""
                  )}
                </td>
                {months.map((monthLabel, colIdx) => {
                  const monthOrders = placed.filter(
                    (o) =>
                      getMonthYearString(new Date(o.placed_date)) === monthLabel
                  );
                  const totalValue = monthOrders.reduce(
                    (sum, o) => sum + (o.value || 0),
                    0
                  );
                  const count = monthOrders.length;

                  const monthDateObj = new Date(monthLabel);

                  const beforeFirstOrder =
                    firstPlaced &&
                    monthDateObj <
                      new Date(
                        firstPlaced.getFullYear(),
                        firstPlaced.getMonth(),
                        1
                      );

                  const afterChurn =
                    !isActiveCustomer(group) &&
                    lastPlaced &&
                    monthDateObj >
                      new Date(
                        lastPlaced.getFullYear(),
                        lastPlaced.getMonth(),
                        1
                      );

                  const monthFirstPlaced = firstPlaced
                    ? getMonthYearString(firstPlaced)
                    : null;
                  const monthLastPlaced = lastPlaced
                    ? getMonthYearString(lastPlaced)
                    : null;

                  const isFirstOrderMonth = monthFirstPlaced === monthLabel;
                  const isChurnedMonth =
                    monthLastPlaced === monthLabel && !isActiveCustomer(group);
                  const isSimultaneousNewChurn =
                    isFirstOrderMonth && isChurnedMonth;

                  let cellStyle = {};
                  if (isFirstOrderMonth) {
                    cellStyle = { backgroundColor: "#d1e7dd" };
                  } else if (isChurnedMonth) {
                    cellStyle = { backgroundColor: "#f8d7da" };
                  }

                  if (beforeFirstOrder || afterChurn) {
                    return <td key={colIdx} style={cellStyle}></td>;
                  }

                  if (count === 0 || totalValue === 0) {
                    return (
                      <td
                        key={colIdx}
                        style={cellStyle}
                        className={`${hasChurnOrder && "bg-DDDDDD"}`}
                      ></td>
                    );
                  }

                  return (
                    <td
                      key={colIdx}
                      style={cellStyle}
                      className={`text-end ${
                        isSimultaneousNewChurn
                          ? "bg-warning-subtle"
                          : isFirstOrderMonth
                          ? "bg-success text-white"
                          : isChurnedMonth
                          ? "bg-danger-subtle"
                          : "bg-success-subtle"
                      }`}
                    >
                      <div className="d-inline-flex align-items-center justify-content-center gap-1">
                        {count > 1 && (
                          <Badge
                            color="light"
                            pill
                            className="border text-muted small px-2"
                          >
                            {count}
                          </Badge>
                        )}
                        <span>{formatCurrency(totalValue)}</span>
                      </div>
                    </td>
                  );
                })}
                <td>
                  {cartValue > 0 ? (
                    <div className="d-inline-flex align-items-center justify-content-center gap-1">
                      <span>{formatCurrency(cartValue)}</span>
                      {cartCount > 1 && (
                        <Badge
                          color="light"
                          pill
                          className="border text-muted small px-2"
                        >
                          {cartCount}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td
                  className="text-muted text-nowrap"
                  style={{ fontSize: "0.8em" }}
                  title={
                    group.lastActivity
                      ? new Date(group.lastActivity).toLocaleDateString()
                      : "-"
                  }
                >
                  {group.lastActivity
                    ? getRelativeTime(group.lastActivity)
                    : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {selectedOrgId && (
        <OrgSummaryPane
          organizationId={selectedOrgId}
          onClose={() => setSelectedOrgId(null)}
        />
      )}
    </Row>
  );
};

export default Customers;
